/* eslint-disable unicorn/no-null */
import { memo, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

// mantine
import {
    MultiSelect,
    ScrollArea,
    Select,
    TagsInput,
    UnstyledButton
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
// components
import { DateRangeSelect, DateRangeKey } from '@newo/ui-kit';
// utils
import useSessionsStore from '@/pages/Sessions/Sessions.store';
import styles from '@/pages/Sessions/components/Filters/Filters.module.css';
import {
    stringToBoolean,
    booleanToString,
    localDateToUTC,
    utcDateToLocal
} from '@/utils/filters';
import { DATE_FORMAT } from '@/constants';

interface FiltersProps {
    total: number;
}

const Filters = ({ total }: FiltersProps) => {
    const { filters, setFilters, resetFilters, dateRange, setDateRange } =
        useSessionsStore(
            useShallow((state) => ({
                filters: state.filters,
                setFilters: state.setFilters,
                resetFilters: state.resetFilters,
                dateRange: state.dateRange,
                setDateRange: state.setDateRange
            }))
        );

    const handleLeadChange = (value: string | null) => {
        setFilters({ is_lead: stringToBoolean(value ?? undefined) });
    };

    const handleTestChange = (value: string | null) => {
        setFilters({ is_test: stringToBoolean(value ?? undefined) });
    };

    const handleTodoChange = (value: string | null) => {
        setFilters({ is_todo: stringToBoolean(value ?? undefined) });
    };

    const handleArchivedChange = (value: string | null) => {
        setFilters({
            is_archived: stringToBoolean(value ?? undefined)
        });
    };

    const handleOpportunityChange = (value: string | null) => {
        setFilters({
            is_opportunity: stringToBoolean(value ?? undefined)
        });
    };

    const handleAgentSuccessChange = (value: string | null) => {
        setFilters({
            agent_success: value ? Number(value) : undefined
        });
    };

    const handleAgentHumanSuccessChange = (value: string | null) => {
        setFilters({
            agent_success_human: value ? Number(value) : undefined
        });
    };

    const handleSessionTypeChange = (value: string[]) => {
        setFilters({ session_types: value });
    };

    const handleFromDateTimeChange = (value: Date | null) => {
        if (value) {
            setDateRange(undefined);
        }
        setFilters({
            from_datetime: value ? localDateToUTC(value) : undefined
        });
    };

    const handleToDateTimeChange = (value: Date | null) => {
        if (value) {
            setDateRange(undefined);
        }
        setFilters({
            to_datetime: value ? localDateToUTC(value) : undefined
        });
    };

    const handleDateTimeChange = (value: DateRangeKey | null) => {
        setDateRange(value ?? undefined);
        handleFromDateTimeChange(null);
        handleToDateTimeChange(null);
    };

    const handleChannelsChange = (value: string[]) => {
        setFilters({
            channels: value
        });
    };

    const handleSessionLengthsChange = (value: string[]) => {
        setFilters({
            session_lengths: value
        });
    };

    const handleReset = () => {
        resetFilters();
    };

    const dateRangeOptions = useMemo(
        () =>
            Object.values(DateRangeKey).map((key) => ({
                value: key,
                label: key
                    .replace(/^last_/, 'Last ')
                    .replaceAll('_', ' ')
                    .replaceAll(/\b\w/g, (m) => m.toUpperCase())
            })),
        []
    );
    return (
        <aside className={styles.filters}>
            <div className="flex justify-between text-xs">
                <span>
                    Found {total} session{total === 1 ? '' : 's'}
                </span>
                <UnstyledButton
                    className="text-xs text-blue-6"
                    onClick={handleReset}
                >
                    Reset
                </UnstyledButton>
            </div>
            <ScrollArea
                className={styles.scrollWrapper}
                mah="100%"
                offsetScrollbars="y"
                type="never"
            >
                <div className="flex flex-col gap-2 pb-20">
                    <TagsInput
                        label="Session Types"
                        placeholder="Add session type"
                        size="xs"
                        value={filters.session_types ?? []}
                        onChange={handleSessionTypeChange}
                    />
                    <Select
                        clearable
                        data={[
                            { value: 'true', label: 'Lead' },
                            { value: 'false', label: 'Non-lead' }
                        ]}
                        label="Is Lead"
                        placeholder="Select"
                        size="xs"
                        value={booleanToString(filters.is_lead) ?? null}
                        onChange={handleLeadChange}
                    />
                    <Select
                        clearable
                        data={[
                            { value: 'true', label: 'Opportunity' },
                            { value: 'false', label: 'Not Opportunity' }
                        ]}
                        label="Is Opportunity"
                        placeholder="Select"
                        size="xs"
                        value={booleanToString(filters.is_opportunity) ?? null}
                        onChange={handleOpportunityChange}
                    />
                    <Select
                        clearable
                        data={[
                            { value: 'true', label: 'Test' },
                            { value: 'false', label: 'Live' }
                        ]}
                        label="Is Test"
                        placeholder="Select"
                        size="xs"
                        value={booleanToString(filters.is_test) ?? null}
                        onChange={handleTestChange}
                    />

                    <Select
                        clearable
                        data={[
                            { value: 'true', label: 'Todo' },
                            { value: 'false', label: 'Not Todo' }
                        ]}
                        label="Is Todo"
                        placeholder="Select"
                        size="xs"
                        value={booleanToString(filters.is_todo) ?? null}
                        onChange={handleTodoChange}
                    />
                    <Select
                        clearable
                        data={[
                            { value: 'true', label: 'Archived' },
                            { value: 'false', label: 'Not Archived' }
                        ]}
                        label="Is Archived"
                        placeholder="Select"
                        size="xs"
                        value={booleanToString(filters.is_archived) ?? null}
                        onChange={handleArchivedChange}
                    />
                    <Select
                        clearable
                        data={[
                            { value: '1', label: 'Success-A' },
                            { value: '2', label: 'Success-B' },
                            { value: '0', label: 'No' }
                        ]}
                        label="Human Assessment"
                        placeholder="Select"
                        size="xs"
                        value={
                            filters.agent_success_human
                                ? `${filters.agent_success_human}`
                                : null
                        }
                        onChange={handleAgentHumanSuccessChange}
                    />
                    <Select
                        clearable
                        data={[
                            { value: '1', label: 'Success-A' },
                            { value: '2', label: 'Success-B' },
                            { value: '0', label: 'No' }
                        ]}
                        label="Auto Assessment"
                        placeholder="Select"
                        size="xs"
                        value={
                            filters.agent_success === undefined
                                ? ''
                                : `${filters.agent_success}`
                        }
                        onChange={handleAgentSuccessChange}
                    />
                    <MultiSelect
                        clearable
                        data={[
                            { value: 'text', label: 'Text' },
                            { value: 'voice', label: 'Voice' },
                            { value: 'technical', label: 'Technical' }
                        ]}
                        label="Channels"
                        placeholder="Select"
                        size="xs"
                        value={filters.channels}
                        onChange={handleChannelsChange}
                    />
                    <MultiSelect
                        clearable
                        data={[
                            { value: 'zero', label: 'Zero' },
                            { value: 'short', label: 'Short' },
                            { value: 'regular', label: 'Regular' },
                            { value: 'long', label: 'Long' }
                        ]}
                        label="Session Lengths"
                        placeholder="Select"
                        size="xs"
                        value={filters.session_lengths}
                        onChange={handleSessionLengthsChange}
                    />
                    <DateRangeSelect
                        options={dateRangeOptions}
                        size="xs"
                        value={dateRange ?? null}
                        onChange={handleDateTimeChange}
                    />
                    <DateTimePicker
                        clearable
                        label="From datetime"
                        placeholder="Pick date and time"
                        size="xs"
                        timeInputProps={{
                            withSeconds: false
                        }}
                        value={
                            filters.from_datetime
                                ? utcDateToLocal(filters.from_datetime)
                                : null
                        }
                        valueFormat={DATE_FORMAT.SHORT_24H}
                        onChange={handleFromDateTimeChange}
                    />
                    <DateTimePicker
                        clearable
                        label="To datetime"
                        placeholder="Pick date and time"
                        size="xs"
                        timeInputProps={{
                            withSeconds: false
                        }}
                        value={
                            filters.to_datetime
                                ? utcDateToLocal(filters.to_datetime)
                                : null
                        }
                        valueFormat={DATE_FORMAT.SHORT_24H}
                        onChange={handleToDateTimeChange}
                    />
                </div>
            </ScrollArea>
        </aside>
    );
};

export default memo(Filters);
